import { useGetAllPresencesQuery, useGetAllUsersForPresenceQuery, useInsertGuestUserMutation, useUpsertPresenceMutation } from "../../slices/usersApiSlice";
import { useGetAllGroupsQuery } from "../../slices/groupsApiSlice";
import { useContext, useEffect, useState } from "react";
import FormInput from "../../components/FormInput";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dataApplicationsContext from "../../context/dataApplicationsContext";
import FormDate from "../../components/FormDate";
import { format, isSameDay } from "date-fns";
import { SyncLoader } from "react-spinners";
import Select from 'react-select';


const Presence = () => {
  // ────────────────────────────────────────────────
  // Contexte et données (RTK Query)
  // ────────────────────────────────────────────────
  const { selectedSeason, selectedGroup, setSelectedGroup } = useContext(dataApplicationsContext);

  const { data: usersP, isLoading: isLoadingUsersP, isSuccess: isSuccessUsersP } = useGetAllUsersForPresenceQuery(selectedSeason?.value, { skip: !selectedSeason });
  const { data: groups, isLoading: isLoadingGroups, isSuccess: isSuccessGroups } = useGetAllGroupsQuery(selectedSeason?.value, { skip: !selectedSeason });
  const { data: presences, isLoading: isLoadingPresences, isSuccess: isSuccessPresences } = useGetAllPresencesQuery(selectedSeason?.value, { skip: !selectedSeason });

  const [upsertPresence] = useUpsertPresenceMutation();
  const [insertGuestUser] = useInsertGuestUserMutation();

  // ────────────────────────────────────────────────
  // États locaux
  // ────────────────────────────────────────────────
  const [displayUsers, setDisplayUsers] = useState([]);       // lignes affichées (coachs + gymnastes + invités)
  const [displayPresence, setDisplayPresence] = useState([]); // présences du jour (et du groupe si sélectionné)
  const [openAddGuest, setOpenAddGuest] = useState(false);    // formulaire d'ajout d'un essai
  const [search, setSearch] = useState('');

  const handleSearch = (e) => {
    setSearch(e.target.value);
  }

  const handleReset = () => {
    setSearch('')
  }

  // ────────────────────────────────────────────────
  // Formulaire (react-hook-form) : date de présence + ajout d'un essai
  // ────────────────────────────────────────────────
  const defaultValues = {
    presence_date: new Date(),
  }

  const schema = yup.object().shape({
  });

  const form = useForm({
    defaultValues, resolver: yupResolver(schema)
  });

  const { register, control, watch, setValue, formState: { errors }, clearErrors, reset, handleSubmit } = form;

  // ────────────────────────────────────────────────
  // Construction de la liste affichée
  // Se recalcule quand les filtres ou les données changent
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (isSuccessGroups && isSuccessUsersP && isSuccessPresences) {
      let filteredUsers = usersP;

      // 0. Les coachs assistants n'apparaissent que si un groupe est sélectionné
      if (!selectedGroup) {
        filteredUsers = filteredUsers.filter(u => !u.is_coach_assistant);
      }

      // 1. Filtre par groupe (fonctionne aussi pour les coachs : ils ont le même format `groups`)
      if (selectedGroup) {
        filteredUsers = filteredUsers.filter(u => u.groups?.some(g => g.id_group === selectedGroup?.value));
      }

      // 2. Filtre par recherche texte (insensible aux accents et à la casse)
      if (search.length > 0) {
        const normalizeString = (str) =>
          str?.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        const normalizedSearch = normalizeString(search);
        const includesSearch = (str) => normalizeString(str)?.includes(normalizedSearch);

        filteredUsers = filteredUsers.filter((user) =>
          includesSearch(user.last_name)
          || includesSearch(user.first_name)
          || includesSearch(user?.birth_date?.split('T')?.[0])
          || includesSearch(user.adresse)
        );
      }

      // 3. Une ligne par couple (personne, groupe)
      const rows = filteredUsers.flatMap((user) => {
        const userGroups = user.groups ?? [];

        // Groupe sélectionné : une seule ligne, sur ce groupe
        if (selectedGroup) {
          return [{
            ...user,
            id_group: selectedGroup.value,
            lib_group: selectedGroup.label,
            showGroup: false,
          }];
        }

        // Aucun groupe connu : on garde l'id_group existant éventuel
        if (userGroups.length === 0) {
          return [{ ...user, id_group: user.id_group ?? null, lib_group: null, showGroup: false }];
        }

        // Sinon : une ligne par groupe, avec le libellé du groupe si la personne en a plusieurs
        return userGroups.map((g) => ({
          ...user,
          id_group: g.id_group,
          lib_group: g.lib_group,
          showGroup: userGroups.length > 1,
        }));
      });

      setDisplayUsers(rows);

      // Présences : on ne garde que celles de la date choisie (et du groupe si sélectionné)
      setDisplayPresence(presences.filter(item => {
        const matchDate = isSameDay(new Date(item.presence_date), new Date(watch('presence_date')));
        const matchGroup = selectedGroup ? item.id_group === selectedGroup?.value : true;
        return matchDate && matchGroup;
      }));
    }
  }, [
    search, selectedGroup, watch('presence_date'), selectedSeason,
    isSuccessPresences, presences, isSuccessGroups, isSuccessUsersP, usersP
  ]);


  // ────────────────────────────────────────────────
  // Ajout d'un essai (invité) dans le groupe sélectionné
  // ────────────────────────────────────────────────
  const onSubmit = async (data) => {
    const { first_name, last_name, telephone_1, telephone_2 } = data;
    await insertGuestUser({
      first_name, last_name, birth_date: null, telephone_1, telephone_2, id_group: selectedGroup?.value
    })
    setOpenAddGuest(false)
  }


  // ────────────────────────────────────────────────
  // Écran de chargement
  // ────────────────────────────────────────────────
  if (isLoadingGroups || isLoadingUsersP || isLoadingPresences) {
    return (
      <div className="main-loader-container">
        <div className="main-loader">
          <img src="/images/logo_cgv.png" alt="Error-404" className="logo-loader"></img>
          <SyncLoader color='#e1004a' size={16}/>
        </div>
      </div>
    )
  }

  // ────────────────────────────────────────────────
  // Gestion des présences
  // ────────────────────────────────────────────────

  // Une présence correspond à une personne (adhérent, coach ou invité) ET à un groupe.
  // Les != null évitent que null === null matche n'importe quelle présence d'invité.
  const findPresence = (row) => displayPresence.find((presence) => {
    const sameGroup = presence.id_group === row.id_group;
    const sameUser =
      (row.id_user != null && presence.id_user === row.id_user) ||
      (row.id_user_guest != null && presence.id_user_guest === row.id_user_guest);
    return sameGroup && sameUser;
  });

  // Coche / décoche une personne : insert si aucune présence, sinon inverse is_present
  const handleCheck = async (id_user, id_user_guest, id_group, isInserted, isCoach = false) => {
    try {
      const idGroup = selectedGroup?.value || id_group;
      const QuoiComment = isInserted ? 'update' : 'insert';
      await upsertPresence({
        QuoiComment, id_user, id_user_guest, id_group: idGroup,
        ...(isCoach && { role: 'coach' }),
        is_present: isInserted ? !isInserted.is_present : true,
        presence_date: format(new Date(watch('presence_date')), 'yyyy-MM-dd 12:00:00')
      }).unwrap();
    } catch (error) {
      console.log(error)
    }
  }

  // ────────────────────────────────────────────────
  // Séparation coachs / gymnastes (le rendu est fait en deux blocs)
  // ────────────────────────────────────────────────
  const coachRows = displayUsers.filter((u) => u.is_coach_assistant);
  const gymRows = displayUsers.filter((u) => !u.is_coach_assistant);

  // Compteur et "tout cocher" : limités aux gymnastes (coachs exclus)
  // Pour inclure les coachs, remplacer gymRows par displayUsers ci-dessous
  const presentCount = gymRows.filter((user) => findPresence(user)?.is_present).length;
  const allChecked = gymRows.length > 0 && gymRows.every((user) => findPresence(user)?.is_present);

  const handleToggleAll = async () => {
    // Le !! force undefined à devenir false avant la comparaison :
    // si tout est coché, on décoche tout ; sinon on coche ceux qui ne le sont pas encore
    const usersToToggle = gymRows.filter((user) => !!findPresence(user)?.is_present === allChecked);
    await Promise.all(usersToToggle.map((user) => {
      const isInserted = findPresence(user);
      return handleCheck(user.id_user, user.id_user_guest, user.id_group, isInserted);
    }));
  }

  const handleSelect = (option) => {
    setSelectedGroup(option)
  }

  const optionsGroups = groups?.map(group => ({ label: group.lib_group, value: group.id_group }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={`presence-container`} noValidate>
      <div className="search-container w-full mb-4">
        <i className={'bi bi-search input-icon'}></i>
        <input
          type={'text'} className={`search-input`} autoComplete="off" autoCorrect="off"
          placeholder={'Rechercher'} value={search} onChange={handleSearch}
        />
        {search.length > 0 && <i className="bi bi-x-lg close-search" onClick={handleReset}></i>}
      </div>
      <Select
        options={optionsGroups} onChange={handleSelect} value={selectedGroup} isClearable={true}
        placeholder="Choisissez une groupe"
        className="input-select-container mb-2" classNamePrefix={"input-select"} isSearchable={true}
      />
      {!openAddGuest &&
        <FormDate
          control={control} setValue={setValue} watch={watch} clearErrors={clearErrors}
          labelClass='form-label date mb-4' noTime placeholder={"26/08/2025"}
          nameInput={`presence_date`} dateClass={`date-input w-full bg-white`}
          dateContainerClass={`input-admin-container input-calendar-container w-full mb-2`}
        />
      }
      <div className="mt-6">
        <div className="flex justify-between">
          {(gymRows.length > 0 && !openAddGuest) &&
            <p className={`presence-count expand`}>{presentCount} présent(s)</p>
          }
          {selectedGroup && !openAddGuest &&
            <div className="flex gap-2">
              <button type="button" className="btn-border-icon" onClick={handleToggleAll}>
                <i className={`bi ${allChecked ? 'bi-x' : 'bi-check2-all'} text-3xl`}></i>
              </button>
            </div>
          }
        </div>
        {(coachRows.length > 0 && !openAddGuest) && (
          <div className="coach-block mt-4 mb-10">
            <p className="coach-block-title">
              Coach assistant{coachRows.length > 1 ? 's' : ''}
            </p>
            {coachRows.map((coach) => {
              const isInserted = findPresence(coach);
              const checkboxId = `coach-presence-${coach.id_user}-${coach.id_group}`;
              return (
                <div className="line-checkbox-container simpleCheckbox mt-3 flex gap-2" key={checkboxId}>
                  <label htmlFor={checkboxId} className="form-label cursor-pointer font-medium">
                    {coach.last_name.toUpperCase()} {coach.first_name}
                  </label>
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={isInserted ? isInserted.is_present : false}
                    // un coach n'est jamais un invité : id_user_guest = null
                    onChange={() => handleCheck(coach.id_user, null, coach.id_group, isInserted, true)}
                  />
                  <label htmlFor={checkboxId}><span></span></label>
                </div>
              );
            })}
          </div>
        )}

        {/* ────────── Liste des gymnastes et des essais ────────── */}
        {(gymRows.length > 0 && !openAddGuest) &&
          gymRows.map((user, index) => {
            // Clé unique par couple (personne, groupe)
            const rowKey = `${user.id_user ?? 'guest-' + user.id_user_guest}-${user.id_group ?? 'none'}`;

            // Le reste à payer n'est calculé que sur la 1re ligne de chaque personne
            const isFirstRowOfUser = gymRows.findIndex(
              u => u.id_user === user.id_user && u.id_user_guest === user.id_user_guest
            ) === index;

            let remainingToDisplay = isFirstRowOfUser ? user.remaining_amount : null;

            // Fratrie : le reste à payer est cumulé et affiché une seule fois (sur le 1er membre)
            if (isFirstRowOfUser && user.id_fraternity) {
              const firstIndex = gymRows.findIndex(u => u.id_fraternity === user.id_fraternity);

              if (firstIndex !== index) {
                remainingToDisplay = null;
              } else {
                // Chaque personne n'est comptée qu'une fois, même avec plusieurs lignes (plusieurs groupes)
                const fratrieMembers = new Map();
                gymRows
                  .filter(u => u.id_fraternity === user.id_fraternity)
                  .forEach(u => fratrieMembers.set(u.id_user, u));

                const total = [...fratrieMembers.values()].reduce((sum, u) => sum + u.remaining_amount, 0);
                remainingToDisplay = total - parseInt(user.discount_fratrie, 10);
              }
            }

            const isInserted = findPresence(user);
            const checkboxId = `user-presence-${rowKey}`;

            return (
              <div
                className={`${remainingToDisplay > 0 ? 'unPaid' : 'pr-2'} mt-3`}
                key={rowKey}
              >
                <div className={`line-checkbox-container simpleCheckbox mt-0 flex gap-2`}>
                  <label htmlFor={checkboxId} className={`form-label cursor-pointer font-medium`}>
                    {user.last_name?.toUpperCase()} {user.first_name}
                    {/* année de naissance, ou "essai" pour un invité sans date */}
                    <span className="font-light ml-2">
                      ({user?.birth_date ? user.birth_date.split('-')?.[0] : 'essai'})
                    </span>
                    {/* nom du groupe affiché seulement si la personne est dans plusieurs groupes */}
                    {user.showGroup && (
                      <span className="font-light ml-2 text-sm">– {user.lib_group}</span>
                    )}
                  </label>
                  <input
                    id={checkboxId}
                    type='checkbox'
                    checked={isInserted ? isInserted.is_present : false}
                    onChange={() => handleCheck(user.id_user, user.id_user_guest, user.id_group, isInserted)}
                  />
                  <label htmlFor={checkboxId}>
                    <span></span>
                  </label>
                </div>
                {remainingToDisplay > 0 &&
                  <p className="font-light">Reste à régler : <span className="ml-3">{remainingToDisplay}€</span></p>
                }
              </div>
            )
          })
        }

        {/* Groupe sélectionné sans aucun gymnaste */}
        {(gymRows.length === 0 && selectedGroup && !openAddGuest) &&
          <p>Oupss... Aucun Gymnaste n'est présent dans ce groupe.</p>
        }
      </div>

      {/* Bouton d'ajout d'un essai */}
      {(selectedGroup && !openAddGuest) &&
        <div className="add-guest mt-10">
          <button className="btn-add black" type="button" onClick={() => setOpenAddGuest(!openAddGuest)}>Ajouter un essai</button>
        </div>
      }

      {/* Formulaire d'ajout d'un essai */}
      {openAddGuest &&
        <div>
          <h3 className="font-bold text-xl mb-8">Ajout d'un essai : </h3>
          <FormInput
            nameClass={"form-input"} nameClassContainer={"form-input-container"}
            nameClassLabel={"form-label mb-4"} nameClassLabelContainer={"form-label-container"}
            label='Nom : ' nameInput={`last_name`}
            type={'text'} register={register} errors={errors}
            watch={watch} placeholder={'Saisissez le nom'}
          />
          <FormInput
            nameClass={"form-input"} nameClassContainer={"form-input-container mt-6"}
            nameClassLabel={"form-label mb-4"} nameClassLabelContainer={"form-label-container"}
            label='Prénom : ' nameInput={`first_name`}
            type={'text'} register={register} errors={errors}
            watch={watch} placeholder={'Saisissez le prénom'}
          />
          <FormInput
            nameClass={"form-input"} nameClassContainer={"form-input-container mt-6"}
            nameClassLabel={"form-label mb-4"} nameClassLabelContainer={"form-label-container"}
            label='Téléphone 1 : ' nameInput={`telephone_1`}
            type={'text'} register={register} errors={errors}
            watch={watch} placeholder={'Saisissez le numéro de télépohone'}
          />
          <FormInput
            nameClass={"form-input"} nameClassContainer={"form-input-container mt-6"}
            nameClassLabel={"form-label mb-4"} nameClassLabelContainer={"form-label-container"}
            label='Téléphone 2 : ' nameInput={`telephone_2`}
            type={'text'} register={register} errors={errors}
            watch={watch} placeholder={'Saisissez le numéro de télépohone'}
          />
          <div className="serparator mb-8 mt-12"></div>
          <div className="part-submit gap-6">
            <button type="button" onClick={() => setOpenAddGuest(false)} className={`btn-add py-4`}>Annuler</button>
            <button type="submit" className={`btn-add black py-4`}>Ajouter l'inscription</button>
          </div>
        </div>
      }
    </form>
  )
}

export default Presence